import { HTTP_RESPONSE_STATUS } from 'common/constants/httpMethodsConstants'

import type {
  AppointmentIssuesSet,
  MedicalIssue,
  ProcedureSearchResult,
  ProcedureSearchRow,
} from './medicalIssues.types'

type IssuesResponse = { data?: MedicalIssue[] } | MedicalIssue[] | null | undefined
type IssueResponse = { data?: MedicalIssue } | MedicalIssue | null | undefined
type AppointmentIssuesResponse =
  | { data?: Partial<AppointmentIssuesSet> | MedicalIssue[] }
  | MedicalIssue[]
  | null
  | undefined
type ProcedureSearchResponse = {
  success?: boolean
  used?: ProcedureSearchRow[]
  catalogue?: ProcedureSearchRow[]
  more?: boolean
  short?: boolean
} | null

export const transformMedicalIssuesResponse = (response: IssuesResponse): MedicalIssue[] => {
  if (!response) return []
  if (Array.isArray(response)) return response
  return response.data ?? []
}

export const transformMedicalIssueResponse = (response: IssueResponse): MedicalIssue | null => {
  if (!response) return null
  if ('data' in response) return response.data ?? null
  return response as MedicalIssue
}

// Sites still on the pre-2026-09-10 backend answer with a bare list of issues.
export const transformAppointmentIssuesResponse = (
  response: AppointmentIssuesResponse,
): AppointmentIssuesSet => {
  const payload = Array.isArray(response) ? response : response?.data
  if (Array.isArray(payload)) return { issues: payload, procedures: [] }
  return { issues: payload?.issues ?? [], procedures: payload?.procedures ?? [] }
}

// No envelope on this route; used rows come first, newest bookings on top.
export const transformProcedureSearchResponse = (
  response: ProcedureSearchResponse,
): ProcedureSearchResult => ({
  rows: response?.success ? [...(response.used ?? []), ...(response.catalogue ?? [])] : [],
  more: !!response?.more,
  short: !!response?.short,
})

// The shared base query flattens error bodies to string[] (fetchBaseQuery.ts extractErrorData).
export const getNotFoundMessage = (error: unknown): string | null => {
  const apiError = error as { status?: number; data?: unknown } | undefined
  if (!apiError || apiError.status !== HTTP_RESPONSE_STATUS.NOT_FOUND) return null
  if (Array.isArray(apiError.data) && typeof apiError.data[0] === 'string') {
    return apiError.data[0]
  }
  return 'Not found'
}

// First server-sent message from a flattened error body, or the fallback.
export const getIssueErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = error as { data?: unknown } | undefined
  if (Array.isArray(apiError?.data) && typeof apiError.data[0] === 'string' && apiError.data[0]) {
    return apiError.data[0]
  }
  return fallback
}
